import { useSelector } from 'react-redux'
import { type Nil, RoleName } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Task } from '~/types'
import useHasRole from '~/utils/useHasRole'

/**
 * Utility function to determine if "Mark As Done" should be disabled for a task.
 * This is the pure logic version that doesn't use hooks.
 *
 * @param task - The task to check
 * @param isTaskListOracleIntegrationEnabled - Whether TASK_LIST_ORACLE_INTEGRATION toggle is ON
 * @param isVcrImprovementEnabled - Whether VCR_IMPROVEMENT_IN_TASK_FLOW toggle is ON
 * @param isCurrentUserVCR - Whether the current user has VCR role
 * @returns true if "Mark As Done" should be disabled, false otherwise
 */
export const shouldDisableTaskMarkAsDone = (
  task: Task | Nil,
  isTaskListOracleIntegrationEnabled: boolean,
  isVcrImprovementEnabled: boolean,
  isCurrentUserVCR: boolean,
): boolean => {
  const isForVCR = task?.forChewyVcr
  const shouldUseVCRBehavior = isTaskListOracleIntegrationEnabled && isForVCR

  // If VCR improvement is enabled and user is VCR, allow them to close the task
  if (isVcrImprovementEnabled && isCurrentUserVCR) {
    return false
  }

  if (!shouldUseVCRBehavior) {
    return false
  }

  // Otherwise, disable for VCR tasks
  return true
}

/**
 * Hook to determine if the "Mark As Done" button should be disabled for a task
 * based on VCR-specific business rules.
 *
 * When TASK_LIST_ORACLE_INTEGRATION is enabled and task has forChewyVcr=true:
 * - If VCR_IMPROVEMENT_IN_TASK_FLOW is enabled AND user has VCR role: allow (return false)
 * - Otherwise: disable (return true)
 *
 * @param task - The task to check
 * @returns true if "Mark As Done" should be disabled, false otherwise
 */
export const useTaskMarkAsDoneDisabled = (task: Task | Nil): boolean => {
  const isTaskListOracleIntegrationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.TASK_LIST_ORACLE_INTEGRATION),
  )
  const isVcrImprovementEnabled = useSelector(
    getFeatureToggle(FeatureToggle.VCR_IMPROVEMENT_IN_TASK_FLOW),
  )
  const isCurrentUserVCR = useHasRole(RoleName.VCR)

  return shouldDisableTaskMarkAsDone(
    task,
    isTaskListOracleIntegrationEnabled,
    isVcrImprovementEnabled,
    isCurrentUserVCR,
  )
}
